/**
 * BaseCard component
 * Shared base component for FriendCard and GroupCard
 * Handles all common layout and rendering logic
 */
import React, { useMemo, useContext } from 'react'
import { View, Text, GestureResponderEvent, Platform, type ImageSourcePropType } from 'react-native'
import { Image } from 'expo-image'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import { Trash2, ChevronRight } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { ThemeContext } from '../../../src/theme/ThemeContext'
import { IDLE_COIN_SOURCE } from '../../../constants/assets'
import {
  getFriendCardStyles,
  getCardDimensions,
  getCardContentStyle,
  getAvatarContainerStyle,
  getGroupImageStyle,
  getProfileBadgeStyle,
  getFriendNameStyle,
  getStatTextStyle,
  getRemoveButtonStyle,
  getSelectionIndicatorStyle,
  getCoinBalanceRowStyles,
  getRankCrestStyle,
  getRankLevelPillStyle,
} from '../FriendCard/FriendCard.styles'
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge'
import { PlayerLevelPill } from '../../../src/economy/components/PlayerLevelPill/PlayerLevelPill'
import {
  PLAYER_RANK_TEST_IDS,
  type PlayerRank,
} from '../../../src/economy/constants/playerRanks.constants'
import { FriendAvatar } from '../shared/FriendAvatar'
import { isAvatarUrl, getInitials } from '../../../src/create/helpers/createUi.helper'
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types'
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types'
import {
  WEB_IMAGE_ALT_DECORATIVE,
  webImageAltGroupCover,
  webImageAltProfilePhoto,
} from '../../../src/shared/helpers/imageWebAlt.helper'


export interface BaseCardProps {
  title: string
  /** When omitted or empty, stat line is not shown. Can be string or custom content (e.g. icon + count for GroupCard). */
  subtitle?: string | React.ReactNode
  /** Optional second line below subtitle (e.g. position for GroupCard). */
  secondarySubtitle?: React.ReactNode
  cardWidth: number
  /** When true (e.g. invite modal), use smaller text so it fits in narrow cards. */
  compact?: boolean
  onPress: () => void
  /** Accessible name for the card's press target (A11Y-1). Defaults to `title`. */
  accessibilityLabel?: string
  /** Accessible name for the remove button (A11Y-1). Defaults to "Remove {title}". */
  removeAccessibilityLabel?: string
  testID?: string
  // Image props (for groups)
  imageSource?: ImageSourcePropType
  // Avatar props (for friends)
  avatarId?: DisplayAvatarId | null
  /** Custom layered look; overrides avatarId when present. */
  avatarConfig?: EquippedMap | null
  avatarUrl?: string
  avatarName?: string
  /** When true, show profile badge (Gmail/Facebook pic or initials) at bottom-left of avatar. Replaces old online dot. */
  showProfileBadge?: boolean
  /**
   * Rank crest + "LV N" pill on the avatar (specs/rank-on-social-screens AC-7, design 1b).
   * Null/omitted renders neither — the card then looks exactly as it did before the feature,
   * which is also the loading, failure and monkey state (AC-11/AC-14).
   */
  playerRank?: PlayerRank | null
  // Action buttons
  onRemove?: (e: GestureResponderEvent) => void
  showSelectionIndicator?: boolean
  /** Optional badge (e.g. creator pill) – rendered top-left on the card. */
  badge?: React.ReactNode
  /** Optional coin balance shown in one row (amount then icon), same as friends tab stat line. */
  coinValue?: number
  /** Disables the main press target (e.g. insufficient coins for stake). */
  disabled?: boolean
}

/**
 * BaseCard Component
 * Handles all common rendering logic for FriendCard and GroupCard
 */
export const BaseCard: React.FC<BaseCardProps> = ({
  title,
  subtitle,
  secondarySubtitle,
  cardWidth,
  compact = false,
  onPress,
  accessibilityLabel,
  removeAccessibilityLabel,
  testID,
  imageSource,
  avatarId,
  avatarConfig,
  avatarUrl,
  avatarName,
  showProfileBadge = false,
  playerRank = null,
  onRemove,
  showSelectionIndicator = false,
  badge,
  coinValue,
  disabled = false,
}) => {
  const theme = useTheme()
  const themeMode = useContext(ThemeContext)?.themeMode ?? 'light'
  const styles = useMemo(() => getFriendCardStyles(theme, themeMode), [theme, themeMode])
  const dimensions = useMemo(() => getCardDimensions(cardWidth, compact), [cardWidth, compact])
  const coinBalanceStyles = useMemo(
    () => getCoinBalanceRowStyles(theme, themeMode, dimensions),
    [theme, themeMode, dimensions]
  )

  // Render image or avatar
  const renderImage = () => {
    if (imageSource) {
      // Group image
      return (
        <Image
          source={imageSource}
          style={[getGroupImageStyle(dimensions), styles.avatarImageGroupBg]}
          contentFit="cover"
          alt={webImageAltGroupCover(title)}
        />
      )
    }
    // Friend avatar
    return (
      <FriendAvatar
        avatarId={avatarId}
        avatarConfig={avatarConfig}
        fallbackUrl={avatarUrl && isAvatarUrl(avatarUrl) ? avatarUrl : undefined}
        small={false}
        size={dimensions.friendAvatarSize}
        nameForInitials={avatarName}
        avatarRounded={false}
        style={styles.avatarOnStage}
      />
    )
  }

  const isAvatarCard = !imageSource
  const shouldShowCoinStack = isAvatarCard && typeof coinValue === 'number'
  const shouldShowSubtitle = subtitle != null && subtitle !== '' && !shouldShowCoinStack

  return (
    <View style={[styles.friendCard, !isAvatarCard && styles.friendCardGroup]}>
      <SoundPressable
        tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
        disabled={disabled}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? title}
        style={styles.friendTouchable}
        testID={testID}
      >
        <View style={[styles.cardContent, getCardContentStyle(dimensions), isAvatarCard && styles.cardContentSpread]}>
        {!imageSource && (
          <>
            <View style={styles.cardStageTop} />
            <View style={styles.cardStageBottom} />
            <View style={styles.stageSplitShadow} />
          </>
        )}
        {/* Remove button - separate Pressable so we don't nest button inside button (hydration error on web) */}
        {onRemove && (
          <SoundPressable
            tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
            onPress={(e) => {
              e.stopPropagation()
              onRemove(e)
            }}
            accessibilityRole="button"
            accessibilityLabel={removeAccessibilityLabel ?? `Remove ${title}`}
            style={({ pressed, hovered }) => [
              styles.removeButton,
              getRemoveButtonStyle(dimensions),
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.removeButtonPressed,
              hovered && Platform.OS === 'web' && styles.removeButtonHover,
            ]}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Trash2 size={dimensions.removeButtonSize} color={theme.gray[500]} />
          </SoundPressable>
        )}
        {/* Selection indicator */}
        {showSelectionIndicator && (
          <View style={[styles.selectionIndicator, getSelectionIndicatorStyle(dimensions)]}>
            <Text style={styles.selectionIndicatorText}>✓</Text>
          </View>
        )}
        {badge != null && <View style={styles.cardBadgeContainer}>{badge}</View>}
        {isAvatarCard && (
          <Text
            style={[styles.friendName, styles.friendNameTop, getFriendNameStyle(dimensions)]}
            numberOfLines={compact ? 1 : 2}
            ellipsizeMode="tail"
          >
            {title}
          </Text>
        )}
        <View style={[styles.avatarContainer, getAvatarContainerStyle(dimensions)]}>
          {isAvatarCard && (
            <>
              <View style={styles.podiumShadowPool} pointerEvents="none" />
              <View style={styles.podiumFloorBar} pointerEvents="none" />
            </>
          )}
          {renderImage()}
          {showProfileBadge && (() => {
            const badgeStyles = getProfileBadgeStyle(theme, dimensions, themeMode)
            const hasProfilePic = avatarUrl && isAvatarUrl(avatarUrl)
            return (
              <View
                style={[
                  styles.profileBadge,
                  // Yield the bottom-right corner only when a crest is actually there (AC-7/AC-14).
                  playerRank != null
                    ? styles.profileBadgeCornerLeft
                    : styles.profileBadgeCornerRight,
                  badgeStyles.container,
                ]}
                testID={PLAYER_RANK_TEST_IDS.friendCardProfileChip}
              >
                {hasProfilePic ? (
                  <Image
                    source={{ uri: avatarUrl }}
                    style={[styles.profileBadgeImage, badgeStyles.container]}
                    contentFit="cover"
                    alt={webImageAltProfilePhoto(avatarName)}
                  />
                ) : (
                  <View style={styles.profileBadgeInitials}>
                    <Text style={[styles.profileBadgeInitialsText, badgeStyles.initialsText]}>
                      {avatarName ? getInitials(avatarName) : '?'}
                    </Text>
                  </View>
                )}
              </View>
            )
          })()}
          {isAvatarCard && playerRank != null && (() => {
            const crest = getRankCrestStyle(dimensions)
            return (
              <>
                <PlayerRankBadge
                  rankIndex={playerRank.rank}
                  size={crest.size}
                  style={crest.style}
                />
                <PlayerLevelPill level={playerRank.level} size="md" style={getRankLevelPillStyle()} />
              </>
            )
          })()}
        </View>
        {!isAvatarCard && (
          <Text
            style={[styles.friendName, styles.groupImageCardTitle, getFriendNameStyle(dimensions)]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {title}
          </Text>
        )}
        {shouldShowCoinStack && (
          <View style={coinBalanceStyles.row}>
            <View style={coinBalanceStyles.coinGroup}>
              <Text style={coinBalanceStyles.value}>{coinValue.toLocaleString()}</Text>
              <Image
                source={IDLE_COIN_SOURCE}
                style={coinBalanceStyles.icon}
                contentFit="contain"
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            </View>
            <ChevronRight size={16} color="#4B5563" />
          </View>
        )}
        {shouldShowSubtitle && (
          typeof subtitle === 'string'
            ? <Text style={[styles.statText, getStatTextStyle(dimensions)]}>{subtitle}</Text>
            : subtitle
        )}
        {secondarySubtitle != null && (
          <View style={styles.secondarySubtitleWrap}>{secondarySubtitle}</View>
        )}
        </View>
      </SoundPressable>
    </View>
  )
}

